import { Link, useNavigate, useOutletContext } from '@remix-run/react'
import { Button } from '#app/components/ui/button'
import { WelcomeVideo } from '#app/components/welcome-video'

export function MapWelcome({ about = false }: { about?: boolean }) {
	const navigate = useNavigate()
	const context = useOutletContext<{ onExplore?: () => void }>()
	const explore = () => {
		if (!about && context?.onExplore) context.onExplore()
		else navigate('/?map=1')
	}
	return (
		<article className="panel-copy map-welcome">
			<h1>
				{about ? 'A map of places that matter.' : 'Every place has a story.'}
			</h1>
			<p>
				{about
					? 'Erda is a shared map of meaningful places. Claim a place, add your memory and share it with someone.'
					: 'Where you met. Where you grew up. Where everything changed. Make that place yours on Erda.'}
			</p>
			<p className="panel-key-message">
				<strong>Your first place is free.</strong> One exclusive place per
				account. No card or renewal fee.
			</p>
			<div className="welcome-actions">
				<Button onClick={explore}>Find my free place</Button>
				<Button asChild variant="outline">
					<Link to={about ? '/digital-gift' : '/info'}>
						{about ? 'Give a place' : 'How it works'}
					</Link>
				</Button>
			</div>
			{!about && <WelcomeVideo />}
			{about && (
				<>
					<section>
						<h2>From a place to a story</h2>
						<ol>
							<li>
								Search or zoom in, then tap an available place on the map.
							</li>
							<li>Sign in, give it a title and add a story if you like.</li>
							<li>Claim it for free, then share your place.</li>
						</ol>
						<p>
							A selection isn’t a reservation. It becomes yours when your claim
							is confirmed.
						</p>
					</section>
					<section>
						<h2>What’s free? What’s paid?</h2>
						<p>
							Each verified account can claim one available place for free. It’s
							exclusively yours on Erda, with no annual fee. Existing members
							can claim one too; existing paid subscriptions stay unchanged.
						</p>
						<p>
							<strong>
								Additional places and gifts cost $2 per place per year.
							</strong>{' '}
							Paid purchases and renewals fund two trees per place. Manage paid
							renewals in your account.
						</p>
					</section>
					<section>
						<h2>Tree support is your choice</h2>
						<p>
							A free place doesn’t fund trees. After claiming, you can
							optionally give $2 once to help fund two trees, or skip it. Your
							place stays free either way.
						</p>
						<Link to="/tree-planting">How tree support works</Link>
					</section>
					<section>
						<h2>Digital ownership, real memories</h2>
						<p>
							Each place is a small hexagon on Erda’s digital map. It’s yours on
							Erda, with no physical land or property rights. Your title, story
							and profile handle are public. You can edit your place from your
							account.
						</p>
						<p>
							Already taken? Choose an available place nearby. Purple means
							owned; black marks your selection.
						</p>
					</section>
					<nav>
						<Link to="/leaderboard">Community impact</Link>
						<Link to="/contact">Contact us</Link>
						<Link to="/terms">Terms</Link>
						<Link to="/privacy">Privacy</Link>
					</nav>
				</>
			)}
			{!about && (
				<p className="text-sm">
					Add a memory. Share your place. Support tree planting if you like.
				</p>
			)}
		</article>
	)
}
